import { createScottApiClient } from "@scott/scott-api";
import { getScottApiClient } from "./get-scott-api-client";

export interface ShopperScottApiClient {
  client: ReturnType<typeof createScottApiClient>;
  /**
   * True when the client carries a signed-in shopper's own OCC token, and the
   * response therefore reflects that shopper rather than an anonymous session.
   * Responses of a personalised client must never enter a shared cache.
   */
  isPersonalised: boolean;
}

/**
 * Scott API client bound to a shopper's OCC token, or the shared anonymous
 * client when there is none.
 *
 * SAP Commerce resolves the base store's B2C consumer price list and the
 * promotion engine against the authenticated session. A shopper in a promoted
 * customer group (PRO/VIP) therefore only receives their own discount when the
 * catalogue request carries their CDC-issued OCC token; with the shared
 * `client_credentials` token SAP returns the standard promotions instead.
 *
 * The token is passed in rather than read here: this module is reachable from
 * the client bundle (see `services/product/shopper-catalogue.ts`), which reads
 * the session behind a server boundary and injects the result.
 */
export async function getScottApiClientForShopper(
  shopperToken?: string | null
): Promise<ShopperScottApiClient> {
  const token = shopperToken?.trim();

  if (!token) {
    return { client: await getScottApiClient(), isPersonalised: false };
  }

  return { client: createScottApiClient(token), isPersonalised: true };
}
